import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Location, OrganisationMember, Team, TeamCapability } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../../components/dashboard/ConfirmDialog';
import { QueryEmptyState } from '../../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { formatPersonName } from '../../lib/people/personName';
import {
  CAPABILITY_OPTIONS,
  formatCapability,
  teamInitials,
} from './teamFormat';

type TeamsListCardProps = {
  teams: Team[] | undefined;
  members: OrganisationMember[] | undefined;
  locations: Location[] | undefined;
  isPending: boolean;
  isError: boolean;
  isRetrying: boolean;
  onRetry: () => void;
  creating: boolean;
  createError: string | null;
  createGeneration: number;
  canManage: boolean;
  onCreate: (input: {
    name: string;
    scope: 'all_locations' | 'locations';
    locationIds: string[];
    capabilities: TeamCapability[];
    memberUserIds: string[];
  }) => void;
  deletingId: string | null;
  onDelete: (teamId: string) => void;
};

export function TeamsListCard({
  teams,
  members,
  locations,
  isPending,
  isError,
  isRetrying,
  onRetry,
  creating,
  createError,
  createGeneration,
  canManage,
  onCreate,
  deletingId,
  onDelete,
}: TeamsListCardProps) {
  const { t } = useTranslation(['teams', 'common']);
  const [composerOpen, setComposerOpen] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'all_locations' | 'locations'>('all_locations');
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<TeamCapability[]>([
    'upload_assets',
    'edit_playlists',
  ]);
  const [memberUserIds, setMemberUserIds] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null);

  useEffect(() => {
    if (createGeneration === 0) return;
    setName('');
    setScope('all_locations');
    setLocationIds([]);
    setCapabilities(['upload_assets', 'edit_playlists']);
    setMemberUserIds([]);
    setComposerOpen(false);
  }, [createGeneration]);

  const count = teams?.length ?? 0;
  const locationScoped = teams?.filter((team) => team.scope === 'locations').length ?? 0;

  const resetComposer = () => {
    setName('');
    setScope('all_locations');
    setLocationIds([]);
    setCapabilities(['upload_assets', 'edit_playlists']);
    setMemberUserIds([]);
    setComposerOpen(false);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate({
      name: trimmed,
      scope,
      locationIds: scope === 'locations' ? locationIds : [],
      capabilities,
      memberUserIds,
    });
  };

  const toggleCapability = (value: TeamCapability) => {
    setCapabilities((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  };

  const toggleLocation = (id: string) => {
    setLocationIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const toggleMember = (userId: string) => {
    setMemberUserIds((current) =>
      current.includes(userId)
        ? current.filter((item) => item !== userId)
        : [...current, userId],
    );
  };

  return (
    <section
      className="rounded-[14px] border border-line px-5 py-5"
      style={{ background: 'var(--gs-panel)' }}
    >
      {deleteTarget ? (
        <ConfirmDialog
          title={t('list.deleteDialog.title', { name: deleteTarget.name })}
          description={t('list.deleteDialog.description')}
          confirmLabel={
            deletingId === deleteTarget.id
              ? t('list.deleteDialog.deleting')
              : t('list.deleteDialog.confirm')
          }
          cancelLabel={t('common:actions.cancel')}
          tone="danger"
          confirming={deletingId === deleteTarget.id}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => {
            onDelete(deleteTarget.id);
            setDeleteTarget(null);
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-base font-bold text-ink">{t('list.title')}</h2>
          <p className="m-0 mt-0.5 text-xs text-muted">
            {count === 0
              ? t('list.subtitleEmpty')
              : t('locationScoped', { count: locationScoped })}
          </p>
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={() => setComposerOpen((open) => !open)}
            className="rounded-[10px] px-3.5 py-2 text-sm font-bold text-[var(--gs-accent-ink)]"
            style={{ background: 'var(--gs-accent-solid)' }}
          >
            {composerOpen ? t('common:actions.close') : t('list.newTeam')}
          </button>
        ) : null}
      </div>

      {composerOpen && canManage ? (
        <form
          onSubmit={submit}
          className="mt-4 space-y-4 rounded-[12px] border border-line px-4 py-4"
          style={{ background: 'var(--gs-panel2)' }}
        >
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-muted" htmlFor="team-name">
              {t('list.nameLabel')}
            </label>
            <input
              id="team-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('list.namePlaceholder')}
              className="w-full rounded-[10px] border border-line px-3 py-2.5 text-sm text-ink outline-none focus:border-[var(--gs-accent)]"
              style={{ background: 'var(--gs-field)' }}
              required
            />
          </div>

          <fieldset className="m-0 border-0 p-0">
            <legend className="mb-1.5 text-xs font-semibold text-muted">
              {t('list.scopeLegend')}
            </legend>
            <div className="flex flex-col gap-2">
              {(
                [
                  { id: 'all_locations' as const, label: t('list.scopeAll') },
                  { id: 'locations' as const, label: t('list.scopeSpecific') },
                ] as const
              ).map((option) => (
                <label
                  key={option.id}
                  className={`flex cursor-pointer items-center gap-2 rounded-[10px] border px-3 py-2 text-sm ${
                    scope === option.id
                      ? 'border-[var(--gs-accent)] text-ink'
                      : 'border-line text-muted'
                  }`}
                >
                  <input
                    type="radio"
                    name="team-scope"
                    checked={scope === option.id}
                    onChange={() => setScope(option.id)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>

          {scope === 'locations' ? (
            <div>
              <p className="mb-1.5 text-xs font-semibold text-muted">{t('list.locationsLabel')}</p>
              {(locations?.length ?? 0) === 0 ? (
                <p className="m-0 text-xs text-muted">{t('list.noLocations')}</p>
              ) : (
                <ul className="m-0 flex max-h-36 list-none flex-col gap-1.5 overflow-y-auto p-0">
                  {locations?.map((location) => (
                    <li key={location.id}>
                      <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                        <input
                          type="checkbox"
                          checked={locationIds.includes(location.id)}
                          onChange={() => toggleLocation(location.id)}
                        />
                        {location.name}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          <div>
            <p className="mb-1.5 text-xs font-semibold text-muted">{t('list.capabilitiesLabel')}</p>
            <div className="flex flex-wrap gap-2">
              {CAPABILITY_OPTIONS.map((option) => {
                const active = capabilities.includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleCapability(option)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                      active
                        ? 'border-[var(--gs-accent)] text-ink'
                        : 'border-line text-muted'
                    }`}
                  >
                    {formatCapability(option)}
                  </button>
                );
              })}
            </div>
          </div>

          {(members?.length ?? 0) > 0 ? (
            <div>
              <p className="mb-1.5 text-xs font-semibold text-muted">{t('list.membersLabel')}</p>
              <ul className="m-0 flex max-h-36 list-none flex-col gap-1.5 overflow-y-auto p-0">
                {members?.map((member) => (
                  <li key={member.userId}>
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={memberUserIds.includes(member.userId)}
                        onChange={() => toggleMember(member.userId)}
                      />
                      {formatPersonName(member, member.email)}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {createError ? <p className="m-0 text-sm text-[var(--gs-bad)]">{createError}</p> : null}

          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={creating || !name.trim() || (scope === 'locations' && locationIds.length === 0)}
              className="rounded-[10px] px-3.5 py-2 text-sm font-bold text-[var(--gs-accent-ink)] disabled:opacity-50"
              style={{ background: 'var(--gs-accent-solid)' }}
            >
              {creating ? t('list.creating') : t('list.create')}
            </button>
            <button
              type="button"
              onClick={resetComposer}
              className="rounded-[10px] border border-line px-3.5 py-2 text-sm font-semibold text-muted"
            >
              {t('common:actions.cancel')}
            </button>
          </div>
        </form>
      ) : null}

      <div className="mt-4">
        {isError || isRetrying ? (
          <QueryErrorState
            message={t('list.loadError')}
            onRetry={onRetry}
            isRetrying={isRetrying}
          />
        ) : isPending ? (
          <QueryLoadingBlock />
        ) : count === 0 && !composerOpen ? (
          <QueryEmptyState>{t('list.empty')}</QueryEmptyState>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {teams?.map((team) => (
              <li
                key={team.id}
                className="rounded-[12px] border border-line px-3 py-3"
                style={{ background: 'var(--gs-panel2)' }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-xs font-bold text-ink"
                    style={{ background: 'var(--gs-field)' }}
                    aria-hidden
                  >
                    {teamInitials(team.name)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="m-0 truncate text-sm font-semibold text-ink">{team.name}</p>
                        <p className="m-0 mt-0.5 text-xs text-muted">
                          {team.scope === 'all_locations'
                            ? t('list.scopeAll')
                            : `${t('list.locationCount', { count: team.locationCount })} · ${t('list.displayCount', { count: team.displayCount })}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted">
                          {t('list.memberCount', { count: team.memberCount })}
                        </span>
                        {canManage ? (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(team)}
                            className="rounded-md px-2 py-1 text-xs font-semibold text-muted hover:text-[var(--gs-bad)]"
                            aria-label={t('list.deleteAria', { name: team.name })}
                          >
                            {t('common:actions.delete')}
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {team.capabilities.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {team.capabilities.map((capability) => (
                          <span
                            key={capability}
                            className="rounded-full border border-line px-2 py-0.5 text-[10px] font-semibold text-muted"
                          >
                            {formatCapability(capability)}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
